// Australian superannuation rates and thresholds by FY (the year it ENDS in).
// Source: ATO "Key superannuation rates and thresholds" and "Caps, limits and
// tax on super contributions". Amounts are WHOLE DOLLARS; rates decimals.
//
// Known gaps (see the finance plan's "future features" list): the maximum
// contribution base before 2021–22 isn't in our source (earlier years use the
// oldest figure, flagged); concessional caps before 2012–13 aren't needed
// (tax tables start 2012–13).

/** Super guarantee charge percentage. */
export function sgRate(fy) {
  if (fy <= 2013) return 0.09;
  if (fy === 2014) return 0.0925;
  if (fy <= 2021) return 0.095;
  if (fy === 2022) return 0.1;
  if (fy === 2023) return 0.105;
  if (fy === 2024) return 0.11;
  if (fy === 2025) return 0.115;
  return 0.12;
}

/**
 * Concessional contributions cap. From 2017–18 it's the same for all ages;
 * before then a higher cap applied from a given age (2013–14: 59+ on 30 June
 * 2013; 2014–15 … 2016–17: 49+ on 30 June of the previous year).
 * @param {number} fy
 * @param {number} [ageOnPriorJune30] — only needed before 2017–18.
 */
export function concessionalCap(fy, ageOnPriorJune30) {
  if (fy >= 2027) return 32_500;
  if (fy >= 2025) return 30_000;
  if (fy >= 2022) return 27_500;
  if (fy >= 2018) return 25_000;
  const age = ageOnPriorJune30 ?? 0;
  if (fy >= 2015) return age >= 49 ? 35_000 : 30_000;
  if (fy === 2014) return age >= 59 ? 35_000 : 25_000;
  return 25_000;
}

/** Annual non-concessional contributions cap. */
export function nonConcessionalCap(fy) {
  if (fy >= 2027) return 130_000;
  if (fy >= 2025) return 120_000;
  if (fy >= 2022) return 110_000;
  if (fy >= 2018) return 100_000;
  if (fy >= 2015) return 180_000;
  return 150_000;
}

/** General transfer balance cap (introduced 1 July 2017); null before. */
export function transferBalanceCap(fy) {
  if (fy >= 2027) return 2_100_000;
  if (fy === 2026) return 2_000_000;
  if (fy >= 2024) return 1_900_000;
  if (fy >= 2022) return 1_700_000;
  if (fy >= 2018) return 1_600_000;
  return null;
}

/**
 * Maximum super contribution base: a QUARTERLY earnings limit up to 2025–26,
 * an ANNUAL one from 2026–27 (Payday Super).
 */
const MCB_QUARTERLY = { 2022: 58_920, 2023: 60_220, 2024: 62_270, 2025: 65_070, 2026: 62_500 };
const MCB_ANNUAL = { 2027: 270_830 };
export function maxContributionBase(fy) {
  const annualYears = Object.keys(MCB_ANNUAL).map(Number);
  if (fy >= Math.min(...annualYears)) {
    const key = Math.min(fy, Math.max(...annualYears));
    return { period: 'year', amount: MCB_ANNUAL[key], fallback: false };
  }
  const years = Object.keys(MCB_QUARTERLY).map(Number);
  const first = Math.min(...years);
  return { period: 'quarter', amount: MCB_QUARTERLY[Math.max(fy, first)], fallback: fy < first };
}

/** Carry-forward of unused concessional cap: TSB limit, first accruing FY, lifetime. */
export const CARRY_FORWARD = { tsbLimit: 500_000, firstUnusedFy: 2019, years: 5 };

/** Excess non-concessional contributions tax rate (if not released). */
export const EXCESS_NCC_RATE = 0.47;

/** The 15% non-refundable offset on excess concessional contributions (from 2013–14). */
export const EXCESS_CC_OFFSET = 0.15;
